import type { UpcomingPatientAppointment } from '@/api/types/ProviderApi'
import { useMemo } from 'react'
import { useFetchUpcomingAppointment } from './useFetchUpcomingAppointment'

export enum UpcomingScheduleEventType {
	NONE = 'NONE',
	APPOINTMENT = 'APPOINTMENT',
	VRDH_CALL = 'VRDH_CALL',
}

export type UpcomingScheduledEvent = {
	appointment?: UpcomingPatientAppointment
	eventType: UpcomingScheduleEventType
}

export const useFetchUpcomingScheduledEvent = () => {
	const {
		appointment: upcomingAppointment,
		isLoading: isLoadingUpcomingAppointment,
		hasUpcomingAppointment,
	} = useFetchUpcomingAppointment()

	const hasLoadedData = useMemo(
		() => !isLoadingUpcomingAppointment,
		[isLoadingUpcomingAppointment]
	)

	const scheduledEvent = useMemo((): UpcomingScheduledEvent => {
		if (!hasLoadedData) {
			return {
				eventType: UpcomingScheduleEventType.NONE,
			}
		}

		const resolveUpcomingScheduledEventType = () => {
			if (hasUpcomingAppointment) {
				return UpcomingScheduleEventType.APPOINTMENT
			}

			return UpcomingScheduleEventType.NONE
		}

		const upcomingScheduledEventType = resolveUpcomingScheduledEventType()

		if (upcomingScheduledEventType === UpcomingScheduleEventType.APPOINTMENT) {
			return {
				appointment: upcomingAppointment!,
				eventType: UpcomingScheduleEventType.APPOINTMENT,
			}
		}

		return {
			eventType: UpcomingScheduleEventType.NONE,
		}
	}, [hasLoadedData, hasUpcomingAppointment, upcomingAppointment])

	const hasScheduledAppointment = useMemo(
		() =>
			hasLoadedData && scheduledEvent.eventType === UpcomingScheduleEventType.APPOINTMENT,
		[hasLoadedData, scheduledEvent]
	)

	const hasNoScheduledEvents = useMemo(
		() => hasLoadedData && scheduledEvent.eventType === UpcomingScheduleEventType.NONE,
		[hasLoadedData, scheduledEvent]
	)

	return {
		isLoading: !hasLoadedData,
		scheduledEvent,
		hasScheduledAppointment,
		hasNoScheduledEvents,
	}
}
